import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (employeeId, password) => {
    const response = await authAPI.login({ employee_id: employeeId, password });
    const { user: userData } = response.data;
    // Note: Only non-sensitive user profile data stored; auth tokens handled via httpOnly cookies if available
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch (_) { /* best-effort */ }
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = () => {
    return ['superadmin', 'divisional_admin', 'admin'].includes(user?.role);
  };

  const isSuperadmin = () => {
    return user?.role === 'superadmin';
  };

  const isDivisionalAdmin = () => {
    return user?.role === 'divisional_admin';
  };

  const isViewer = () => {
    return user?.role === 'viewer';
  };

  const canApprove = () => {
    return ['superadmin', 'divisional_admin', 'admin', 'approving_supervisor'].includes(user?.role);
  };

  const canInspect = () => {
    return ['superadmin', 'divisional_admin', 'admin', 'reporting_officer', 'approving_supervisor', 'supervisor'].includes(user?.role);
  };

  // True when the current user should send `for_user_id` to backend listing
  // endpoints so role-based scoping kicks in. Admin / superadmin / viewer
  // see global data — everyone else is scoped.
  const isScoped = () => {
    return !!user && !['superadmin', 'admin', 'viewer'].includes(user?.role);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isSuperadmin, isDivisionalAdmin, isViewer, canApprove, canInspect, isScoped }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
